import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Trash2Icon } from "lucide-react";
import {
  KnowledgeCheckContent,
  KnowledgeCheckOption,
  KnowledgeCheckQuestion,
  KnowledgeCheckQuestionType,
} from "@/modules/learningCenter/features/CourseManagement/types/course";
import { parseKnowledgeCheck } from "@/modules/learningCenter/features/CourseDirectory/lib/knowledgeCheck";
import { CourseFormBlock } from "@/modules/learningCenter/features/CourseManagement/components/CourseForm/types";

interface KnowledgeCheckBlockEditorProps {
  block: CourseFormBlock;
  onMetadataChange: (metadata: Record<string, unknown> | null) => void;
}

function newOption(label = ""): KnowledgeCheckOption {
  return { id: crypto.randomUUID(), label };
}

function trueFalseOptions(): KnowledgeCheckOption[] {
  return [
    { id: crypto.randomUUID(), label: "True" },
    { id: crypto.randomUUID(), label: "False" },
  ];
}

function newQuestion(): KnowledgeCheckQuestion {
  return {
    id: crypto.randomUUID(),
    type: "single",
    prompt: "",
    options: [newOption(), newOption()],
  };
}

/**
 * Authoring surface for a `knowledge_check` block. Writes the full
 * `{ version: 2, title?, questions }` payload to the block `metadata` on every
 * change (the block `content` stays null).
 */
export function KnowledgeCheckBlockEditor({
  block,
  onMetadataChange,
}: KnowledgeCheckBlockEditorProps) {
  const content = parseKnowledgeCheck(block.metadata);

  const write = (next: KnowledgeCheckContent) => {
    const metadata: Record<string, unknown> = {
      version: 2,
      questions: next.questions,
    };
    if (next.title && next.title.trim() !== "") {
      metadata.title = next.title;
    }
    onMetadataChange(metadata);
  };

  const updateQuestion = (
    id: string,
    updater: (q: KnowledgeCheckQuestion) => KnowledgeCheckQuestion,
  ) => {
    write({
      ...content,
      questions: content.questions.map((q) => (q.id === id ? updater(q) : q)),
    });
  };

  const handleTitleChange = (title: string) => {
    write({ ...content, title });
  };

  const handleAddQuestion = () => {
    write({ ...content, questions: [...content.questions, newQuestion()] });
  };

  const handleRemoveQuestion = (id: string) => {
    write({
      ...content,
      questions: content.questions.filter((q) => q.id !== id),
    });
  };

  const handleTypeChange = (id: string, type: KnowledgeCheckQuestionType) => {
    updateQuestion(id, (q) => ({
      ...q,
      type,
      options: type === "true_false" ? trueFalseOptions() : q.options,
    }));
  };

  const handlePromptChange = (id: string, prompt: string) => {
    updateQuestion(id, (q) => ({ ...q, prompt }));
  };

  const handleExplanationChange = (id: string, explanation: string) => {
    updateQuestion(id, (q) => ({ ...q, explanation }));
  };

  const handleOptionLabelChange = (
    questionId: string,
    optionId: string,
    label: string,
  ) => {
    updateQuestion(questionId, (q) => ({
      ...q,
      options: q.options.map((o) => (o.id === optionId ? { ...o, label } : o)),
    }));
  };

  const handleMarkCorrect = (questionId: string, optionId: string) => {
    updateQuestion(questionId, (q) => ({
      ...q,
      options: q.options.map((o) => ({ ...o, correct: o.id === optionId })),
    }));
  };

  const handleAddOption = (questionId: string) => {
    updateQuestion(questionId, (q) => ({
      ...q,
      options: [...q.options, newOption()],
    }));
  };

  const handleRemoveOption = (questionId: string, optionId: string) => {
    updateQuestion(questionId, (q) => ({
      ...q,
      options: q.options.filter((o) => o.id !== optionId),
    }));
  };

  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <Label htmlFor={`kc-title-${block.id}`}>Title (optional)</Label>
        <Input
          id={`kc-title-${block.id}`}
          onChange={(e) => handleTitleChange(e.target.value)}
          placeholder="e.g. Check your understanding"
          value={content.title ?? ""}
        />
      </div>

      {content.questions.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          No questions yet. Add one below.
        </p>
      ) : (
        content.questions.map((question, index) => (
          <div
            className="space-y-3 rounded-lg border border-gray-200 dark:border-slate-700 p-3"
            key={question.id}
          >
            <div className="flex items-center justify-between gap-2">
              <span className="text-sm text-gray-500 dark:text-gray-400">
                Question {index + 1}
              </span>
              <button
                aria-label={`Remove question ${index + 1}`}
                className="text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                onClick={() => handleRemoveQuestion(question.id)}
                type="button"
              >
                <Trash2Icon className="h-4 w-4" />
              </button>
            </div>

            <div className="space-y-1">
              <Label htmlFor={`kc-type-${question.id}`}>Question type</Label>
              <select
                className="flex h-9 w-full rounded-md border border-input bg-white px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring dark:bg-slate-800 dark:border-slate-700 dark:text-slate-100"
                id={`kc-type-${question.id}`}
                onChange={(e) =>
                  handleTypeChange(
                    question.id,
                    e.target.value as KnowledgeCheckQuestionType,
                  )
                }
                value={question.type}
              >
                <option value="single">Multiple choice</option>
                <option value="true_false">True or False</option>
              </select>
            </div>

            <div className="space-y-1">
              <Label htmlFor={`kc-prompt-${question.id}`}>Prompt</Label>
              <Input
                id={`kc-prompt-${question.id}`}
                onChange={(e) =>
                  handlePromptChange(question.id, e.target.value)
                }
                placeholder="Ask a question…"
                value={question.prompt}
              />
            </div>

            <div className="space-y-2">
              <span className="text-sm text-gray-500 dark:text-gray-400">
                Options (select the correct answer)
              </span>
              {question.options.map((option, optionIndex) => (
                <div className="flex items-center gap-2" key={option.id}>
                  <input
                    aria-label={`Mark option ${optionIndex + 1} of question ${
                      index + 1
                    } correct`}
                    checked={option.correct === true}
                    name={`kc-correct-${question.id}`}
                    onChange={() => handleMarkCorrect(question.id, option.id)}
                    type="radio"
                  />
                  {question.type === "true_false" ? (
                    <span className="flex-1 text-sm text-gray-700 dark:text-gray-200">
                      {option.label}
                    </span>
                  ) : (
                    <>
                      <Input
                        aria-label={`Option ${optionIndex + 1} of question ${
                          index + 1
                        }`}
                        onChange={(e) =>
                          handleOptionLabelChange(
                            question.id,
                            option.id,
                            e.target.value,
                          )
                        }
                        placeholder={`Option ${optionIndex + 1}`}
                        value={option.label}
                      />
                      <button
                        aria-label={`Remove option ${optionIndex + 1} of question ${
                          index + 1
                        }`}
                        className="text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                        onClick={() =>
                          handleRemoveOption(question.id, option.id)
                        }
                        type="button"
                      >
                        <Trash2Icon className="h-4 w-4" />
                      </button>
                    </>
                  )}
                </div>
              ))}
              {question.type === "single" && (
                <Button
                  onClick={() => handleAddOption(question.id)}
                  size="sm"
                  type="button"
                  variant="outline"
                >
                  Add Option
                </Button>
              )}
            </div>

            <div className="space-y-1">
              <Label htmlFor={`kc-explanation-${question.id}`}>
                Explanation (optional)
              </Label>
              <Textarea
                id={`kc-explanation-${question.id}`}
                onChange={(e) =>
                  handleExplanationChange(question.id, e.target.value)
                }
                placeholder="Shown after the learner checks their answer…"
                rows={2}
                value={question.explanation ?? ""}
              />
            </div>
          </div>
        ))
      )}

      <Button
        onClick={handleAddQuestion}
        size="sm"
        type="button"
        variant="outline"
      >
        Add Question
      </Button>
    </div>
  );
}
